'use client'

// Panel de login. NO es una página: vive dentro del content de DashboardPage
// como un tab más (ver components/Pages/DashboardPage.jsx, case 'account').
// Por eso no trae fondo propio ni min-height:100vh — el shell (sidebar +
// AnimatedBackground) ya está montado alrededor.

import React, { useState } from 'react'
import styled from 'styled-components'
import Image from 'next/image'
import { useLanguage } from '@/contexts/LanguageContext'
import AnimatedLogo from '@/components/Layouts/AnimatedLogo'

const ERROR_KEYS = {
    terms: 'LOGIN_ERROR_TERMS',
}

// OJO con globalStyles.js: hay un `div { display:flex; flex-direction:row;
// align-items:flex-start }` global, así que acá abajo TODO div necesita su
// dirección y su align-items explícitos o hereda fila + sin estirar.
const Wrapper = styled.div`
    width: 100%;
    /* min-height:100% no sirve: el contenedor de arriba (ContentScrollArea)
       tiene altura automática. Contra el viewport sí centra de verdad. */
    min-height: calc(100vh - 40px);
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    padding: 40px 24px;
`

const Panel = styled.div`
    width: 100%;
    max-width: 440px;
    background: rgba(13, 17, 23, 0.55);
    backdrop-filter: blur(12px);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 16px;
    padding: 36px 36px 28px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 22px;
    box-shadow: 0 30px 80px rgba(0, 0, 0, 0.45);
    animation: fadeIn 0.4s ease-out;

    @keyframes fadeIn {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
    }
`

const TextBlock = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
`

const Title = styled.h1`
    font-family: 'Chakra Petch', sans-serif;
    font-size: 18px;
    font-weight: 800;
    color: #fff;
    letter-spacing: 0.5px;
    margin: 0;
    text-align: center;
`

const Subtitle = styled.p`
    font-size: 13px;
    color: rgba(255, 255, 255, 0.45);
    line-height: 1.6;
    text-align: center;
    margin: 8px 0 0;
`

const TermsRow = styled.label`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.6);
    line-height: 1.5;
    cursor: pointer;
    user-select: none;
`

const Checkbox = styled.input`
    margin-top: 2px;
    accent-color: #66c0f4;
    cursor: pointer;
`

const SteamButton = styled.a`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 100%;
    padding: 12px 20px;
    border-radius: 10px;
    font-family: 'Chakra Petch', sans-serif;
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.3px;
    text-transform: uppercase;
    text-decoration: none;
    cursor: pointer;
    border: 1px solid rgba(102, 192, 244, 0.4);
    background: linear-gradient(180deg, #1b2838 0%, #0f1620 100%);
    color: #66c0f4;
    transition: all 0.2s;

    &:hover {
        border-color: #66c0f4;
        color: #fff;
        background: linear-gradient(180deg, #223a52 0%, #16212d 100%);
        transform: translateY(-1px);
    }

    &[aria-disabled='true'] {
        opacity: 0.4;
        pointer-events: none;
    }
`

const ErrorMsg = styled.div`
    width: 100%;
    font-size: 12px;
    color: #f87171;
    background: rgba(239, 68, 68, 0.08);
    border: 1px solid rgba(239, 68, 68, 0.2);
    border-radius: 6px;
    padding: 8px 12px;
    text-align: center;
`

const Hint = styled.div`
    font-size: 11px;
    color: rgba(255, 255, 255, 0.25);
    text-align: center;
`

export default function LoginCard({ error }) {
    const { t } = useLanguage()
    const [termsAccepted, setTermsAccepted] = useState(false)

    const errorMessage = error && t(ERROR_KEYS[error] ?? 'LOGIN_ERROR_GENERIC')

    return (
        <Wrapper>
            <Panel>
                <AnimatedLogo />

                <TextBlock>
                    <Title>{t('LOGIN_TITLE')}</Title>
                    <Subtitle>{t('LOGIN_SUBTITLE')}</Subtitle>
                </TextBlock>

                {errorMessage && <ErrorMsg>{errorMessage}</ErrorMsg>}

                <TermsRow>
                    <Checkbox
                        type="checkbox"
                        checked={termsAccepted}
                        onChange={(e) => setTermsAccepted(e.target.checked)}
                    />
                    {t('LOGIN_TERMS')}
                </TermsRow>

                <SteamButton
                    href={termsAccepted ? '/api/auth/steam?terms=1' : undefined}
                    aria-disabled={!termsAccepted}
                    role="button"
                >
                    <Image src="/Steam_icon_logo.png" alt="" width={20} height={20} />
                    {t('LOGIN_STEAM_BUTTON')}
                </SteamButton>

                <Hint>{t('LOGIN_HINT')}</Hint>
            </Panel>
        </Wrapper>
    )
}
